import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldQuestion } from 'lucide-react'
import { ErpModal, ErpButton, ErpDiv, ErpSpan } from '@/components/erp'
import { useApproveAiToolMutation, useRejectAiToolMutation } from '@/redux/api/aiApi'
import { useSwal } from '@/hooks/useSwal'
import type { AiApprovalPrompt } from '@/redux/slices/aiSlice'

interface AiActionApprovalModalProps {
  approval: AiApprovalPrompt | null
  onClose: () => void
}

/**
 * Human-in-the-loop gate: shows the full arguments of a pending AI
 * mutating action before it may execute.
 */
export const AiActionApprovalModal: React.FC<AiActionApprovalModalProps> = ({ approval, onClose }) => {
  const { t } = useTranslation()
  const swal = useSwal()
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [approveAiTool] = useApproveAiToolMutation()
  const [rejectAiTool] = useRejectAiToolMutation()

  if (!approval) return null

  const decide = async (kind: 'approve' | 'reject') => {
    // A ref latch, not `busy`: state only disables the buttons after a
    // re-render, so a rapid double click could fire decide() twice.
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await (kind === 'approve' ? approveAiTool(approval.id) : rejectAiTool(approval.id)).unwrap();
      swal.toastSuccess(
        kind === 'approve'
          ? t('AI_ApprovalDone')
          : t('AI_ApprovalRejected'),
      )
      onClose()
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      swal.toastError(message ?? t('AI_StreamError'))
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  return (
    <ErpModal
      isOpen={true}
      toggle={onClose}
      title={t('AI_ApprovalTitle')}
      size="lg"
      noFooter
      busy={busy}
    >
      <ErpDiv className="space-y-4">
        <ErpDiv className="flex items-center gap-2 text-sm font-medium">
          <ShieldQuestion className="h-5 w-5 text-amber-500" />
          <ErpSpan className="font-mono">{approval.tool}</ErpSpan>
        </ErpDiv>

        {approval.reason && (
          <ErpDiv>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">{t('AI_ApprovalReason')}</p>
            <p className="text-sm">{approval.reason}</p>
          </ErpDiv>
        )}

        <ErpDiv>
          <p className="mb-1 text-xs font-semibold text-muted-foreground">{t('AI_ApprovalArguments')}</p>
          <pre
            dir="ltr"
            className="max-h-64 overflow-auto rounded-lg bg-muted p-3 text-xs leading-relaxed"
          >
            {JSON.stringify(approval.arguments ?? {}, null, 2)}
          </pre>
        </ErpDiv>

        <ErpDiv className="flex justify-end gap-2">
          <ErpButton variant="outline" onClick={() => decide('reject')} disabled={busy}>
            {t('AI_Reject')}
          </ErpButton>
          <ErpButton onClick={() => decide('approve')} disabled={busy} loading={busy}>
            {t('AI_Approve')}
          </ErpButton>
        </ErpDiv>
      </ErpDiv>
    </ErpModal>
  )
}

export default AiActionApprovalModal
